// src/utils/dataApi.ts - Cleaned and reorganized

import { authenticatedFetch } from './authApi';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export const SUBSCRIPTION_LIMITS = {
  free: { storage_gb: 1, label: '1 GB' },
  pro: { storage_gb: 100, label: '100 GB' }, 
  enterprise: { storage_gb: 1000, label: '1 TB' }
} as const;

export interface FileUploadInfo {
  name: string;
  size: number;
  type: string;
}

export interface ValidationConfig {
  method: 'by_device' | 'by_type';
  options: Array<{
    value: string;
    label: string;
    extensions: string[];
    enabled?: boolean;
  }>;
}

export interface Dataset {
  data_id: string;
  project_id: string;
  user_id: string;
  dataset_name: string;
  classification_method: 'by_device' | 'by_type';
  data_type_or_device: string;
  file_count: number;
  total_size_bytes: number;
  upload_status: string;
  validation_status: string;
  validation_issues: string[];
  created_at: string;
  updated_at: string;
}

export interface FileInfo {
  file_id: string;
  filename: string;
  size_bytes: number;
  file_type: string;
  upload_status: string;
  validation_status: string;
  validation_issues: string[];
  created_at: string;
  updated_at: string;
}

export interface UserStorageUsage {
  user_id: string;
  total_size_bytes: number;
  total_files: number;
  datasets_count: number;
  total_size_gb: number;
  subscription_limit_gb: number;
  subscription_tier: 'free' | 'pro' | 'enterprise';
  usage_percentage: number;
}

// ============================================================================
// VALIDATION AND CONFIGURATION
// ============================================================================

/**
 * Get validation configuration for data upload
 */
export const getValidationConfig = async (method: 'by_device' | 'by_type' = 'by_type'): Promise<ValidationConfig> => {
  const response = await authenticatedFetch(`/api/data/validation/config?method=${method}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get validation config');
  }
  
  const data = await response.json();
  return data.config;
};

/**
 * Validate files before upload
 */
export const validateUploadRequest = async (params: {
  files: FileUploadInfo[];
  classification_method: 'by_device' | 'by_type';
  data_type_or_device: string;
}): Promise<{
  valid: boolean;
  issues: string[];
  files: any[];
  total_size: number;
  total_files: number;
}> => {
  const response = await authenticatedFetch('/api/data/validation/validate', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate upload request');
  }
  
  return response.json();
};

// ============================================================================
// UPLOAD MANAGEMENT
// ============================================================================

/**
 * Create dataset and upload files directly - UPDATED with completion call
 */
export const uploadDataset = async (
  projectId: string,
  params: {
    dataset_name: string;
    classification_method: 'by_device' | 'by_type';
    data_type_or_device: string;
    files: File[];
  },
  onProgress?: (completed: number, total: number) => void
): Promise<Dataset> => {
  // First create the dataset
  const response = await authenticatedFetch(`/api/data/${projectId}/dataset`, {
    method: 'POST',
    body: JSON.stringify({
      dataset_name: params.dataset_name,
      classification_method: params.classification_method,
      data_type_or_device: params.data_type_or_device,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create dataset');
  }
  
  const dataset = await response.json();
  
  // Upload files one by one
  let completed = 0;
  for (const file of params.files) {
    // Get signed URL for this file
    const urlResponse = await authenticatedFetch(`/api/data/datasets/${dataset.data_id}/upload-url`, {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        size: file.size
      }),
    });
    
    if (!urlResponse.ok) {
      throw new Error(`Failed to get upload URL for ${file.name}`);
    }
    
    const { upload_url } = await urlResponse.json();
    
    // Upload file directly to GCS
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    
    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload ${file.name}`);
    }
    
    completed++;
    onProgress?.(completed, params.files.length);
  }
  
  // **CRITICAL: Complete the upload process immediately after files finish**
  const completionResult = await completeDatasetUpload(dataset.data_id);
  return completionResult.dataset;
};

// ============================================================================
// DATASET MANAGEMENT
// ============================================================================

/**
 * Complete dataset upload after all files are uploaded to GCS
 */
export const completeDatasetUpload = async (datasetId: string): Promise<{
  dataset: Dataset;
  message: string;
}> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}/complete-upload`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to complete dataset upload');
  }
  
  return response.json();
};

/**
 * Get datasets for a project
 */
export const getProjectDatasets = async (projectId: string): Promise<{
  datasets: Dataset[];
  count: number;
}> => {
  const response = await authenticatedFetch(`/api/data/${projectId}/datasets`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get project datasets');
  }
  
  return response.json();
};

/**
 * Get a specific dataset
 */
export const getDataset = async (datasetId: string): Promise<Dataset> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get dataset');
  }
  
  const data = await response.json();
  return data.dataset;
};

/**
 * Delete a dataset
 */
export const deleteDataset = async (datasetId: string): Promise<void> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete dataset');
  }
};

/**
 * Delete dataset with progress tracking
 */
export const deleteDatasetWithProgress = async (
  datasetId: string,
  onProgress?: (progress: { steps_completed: number; total_steps: number; current_step?: string }) => void
): Promise<void> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}/delete-progress`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete dataset');
  }
  
  const result = await response.json();
  
  // If progress callback provided, simulate step-by-step progress
  if (onProgress) {
    // Step 1: Files
    onProgress({
      steps_completed: 0,
      total_steps: 3,
      current_step: 'Deleting files from cloud storage...'
    });
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Step 2: Database
    onProgress({
      steps_completed: 1,
      total_steps: 3,
      current_step: 'Removing dataset from database...'
    });
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Step 3: Cleanup
    onProgress({
      steps_completed: 2,
      total_steps: 3,
      current_step: 'Cleaning up references...'
    });
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Complete
    onProgress({
      steps_completed: 3,
      total_steps: 3,
      current_step: 'Complete'
    });
  }
};

// ============================================================================
// FILE MANAGEMENT
// ============================================================================

/**
 * Get files in a dataset
 */
export const getDatasetFiles = async (
  datasetId: string,
  params?: {
    status?: 'valid' | 'invalid' | 'pending';
    file_type?: string;
    limit?: number;
    offset?: number;
  }
): Promise<{
  files: FileInfo[];
  count: number;
  total_size: number;
}> => {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.file_type) searchParams.set('file_type', params.file_type);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());

  const url = `/api/data/datasets/${datasetId}/files${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  const response = await authenticatedFetch(url);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get dataset files');
  }
  
  return response.json();
};

/**
 * Get download URL for a file
 */
export const getFileDownloadUrl = async (datasetId: string, fileId: string): Promise<string> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}/files/${fileId}/download`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get download URL');
  }
  
  const data = await response.json();
  return data.download_url;
};

/**
 * Delete a specific file
 */
export const deleteFile = async (datasetId: string, fileId: string): Promise<void> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}/files/${fileId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete file');
  }
};

/**
 * Clear all files from a dataset
 */
export const clearDatasetFiles = async (datasetId: string): Promise<void> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}/files/clear`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to clear dataset files');
  }
};

/**
 * Re-validate dataset files
 */
export const validateDatasetFiles = async (datasetId: string): Promise<void> => {
  const response = await authenticatedFetch(`/api/data/datasets/${datasetId}/validate-files`, {
    method: 'POST',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate files');
  }
};

// ============================================================================
// USER DATA MANAGEMENT
// ============================================================================

/**
 * Get user storage usage
 */
export const getUserStorageUsage = async (): Promise<UserStorageUsage> => {
  const response = await authenticatedFetch('/api/data/user/storage');
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get storage usage');
  }
  
  return response.json();
};

/**
 * Get user upload sessions
 */
export const getUserUploadSessions = async (activeOnly: boolean = false): Promise<{
  sessions: any[];
  count: number;
}> => {
  const response = await authenticatedFetch(`/api/data/user/upload-sessions?active_only=${activeOnly}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get upload sessions');
  }
  
  return response.json();
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${size.toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};